
import {
  NovelActSchema,
  NovelChapterSchema,
  NovelSceneSchema,
  NovelSchema,
  CodexCategorySchema,
  CodexEntrySchema,
  CoreEntityType,
  NovelSeriesSchema,
  SnippetSchema
} from "#/index";
import { CodexDetail, ExecutionContext, NovelLabel, OutlineTree, PointOfView } from "../types";

class Codex {
  context: ExecutionContext;
  result: CodexEntrySchema[] = [];
  private mode: 'union' | 'intersection' = 'union';
  private initialized: boolean = false;

  constructor(context: ExecutionContext, mode: 'union' | 'intersection' = 'union') {
    this.context = context;
    this.mode = mode;
  }

  useUnion(reset: boolean = true): this {
    this.mode = 'union';
    if (reset) {
      this.result = [];
      this.initialized = false;
    }
    return this;
  }

  useIntersection(reset: boolean = true): this {
    this.mode = 'intersection';
    if (reset) {
      this.result = [];
      this.initialized = false;
    }
    return this;
  }

  private toEntry(data?: CodexEntrySchema | string): CodexEntrySchema | undefined {
    if (!data) return undefined;
    if (typeof data !== 'string') {
      return data;
    }
    return this.context!.globalContext.generateStore.getState().codexEntries.find(d => d.id === data);
  }

  private applyFilter(items: Array<CodexEntrySchema | string | undefined>): void {
    const entries = items
      .map(item => this.toEntry(item))
      .filter((entry): entry is CodexEntrySchema => !!entry);

    if (this.mode === 'union') {
      const existedIds = new Set(this.result.map(entry => entry.id));
      entries.forEach(entry => {
        if (!existedIds.has(entry.id)) {
          existedIds.add(entry.id);
          this.result.push(entry);
        }
      });
      return;
    }

    if (!this.initialized) {
      this.result = entries.slice();
      this.initialized = true;
      return;
    }

    const entryIds = new Set(entries.map(entry => entry.id));
    this.result = this.result.filter(entry => entryIds.has(entry.id));
  }

  getResult() {
    return this.result || [];
  }
  byNovel(novel: NovelSchema): Codex {
    const { codexEntries } = this.context!.globalContext.generateStore.getState();
    const entries = codexEntries.filter(entry => entry.relationships.project.type === 'novels' && entry.relationships.project.id === novel.id);
    this.applyFilter(entries);
    return this;
  }
  bySeries(series: NovelSeriesSchema): Codex {
    const { codexEntries } = this.context!.globalContext.generateStore.getState();
    const entries = codexEntries.filter(entry => entry.relationships.project.type === 'series' && entry.relationships.project.id === series.id);
    this.applyFilter(entries);
    return this;
  }
  byOutline(outline: OutlineTree): Codex {
    const entries: Array<CodexEntrySchema | string | undefined> = [];
    outline.data.forEach(act => {
      act.children?.forEach(chapter => {
        chapter.children?.forEach(scene => {
          if (scene.data.type === CoreEntityType.NovelScenes) {
            entries.push(scene.data.attributes.pov?.characterId);
            scene.data.relationships.references.forEach(id => {
              entries.push(id);
            })
          }
        })
      })
    })
    this.applyFilter(entries);
    return this;
  }
  byAct(act: NovelActSchema): Codex {
    const entries: Array<CodexEntrySchema | string | undefined> = [];
    const { novelActsTree } = this.context!.globalContext.generateStore.getState();
    const actTree = novelActsTree.find(d => d.id === act.id);
    actTree?.children?.forEach(chapter => {
      chapter.children?.forEach(scene => {
        if (scene.data.type === CoreEntityType.NovelScenes) {
          entries.push(scene.data.attributes.pov?.characterId);
          scene.data.relationships.references.forEach(id => {
            entries.push(id);
          })
        }
      })
    })
    this.applyFilter(entries);
    return this;
  }
  byChapter(chapter: NovelChapterSchema): Codex {
    const entries: Array<CodexEntrySchema | string | undefined> = [];
    const { novelActsTree } = this.context!.globalContext.generateStore.getState();
    novelActsTree.forEach(act => {
      if (act.childIds?.includes(chapter.id)) {
        act.children?.forEach(chapter => {
          chapter.children?.forEach(scene => {
            if (scene.data.type === CoreEntityType.NovelScenes) {
              entries.push(scene.data.attributes.pov?.characterId);
              scene.data.relationships.references.forEach(id => {
                entries.push(id);
              })
            }
          })
        })
      }
    })
    this.applyFilter(entries);
    return this;
  }
  byScene(scene: NovelSceneSchema): Codex {
    const entries: Array<CodexEntrySchema | string | undefined> = [
      scene.attributes.pov?.characterId,
      ...scene.relationships.references
    ];
    this.applyFilter(entries);
    return this;
  }
  byLabel(label: NovelLabel): Codex {
    const entries: Array<CodexEntrySchema | string | undefined> = [];
    const { novelScenes } = this.context!.globalContext.generateStore.getState();
    novelScenes.forEach(scene => {
      if (scene.relationships.labels.includes(label.data.id)) {
        entries.push(scene.attributes.pov?.characterId);
        scene.relationships.references.forEach(id => {
          entries.push(id);
        })
      }
    })
    this.applyFilter(entries);
    return this;
  }
  byPov(pov: PointOfView): Codex {
    this.applyFilter([pov.characterId]);
    return this;
  }
  byCodexEntry(entry: CodexEntrySchema): Codex {
    this.applyFilter([entry]);
    return this;
  }
  byCodexCategory(category: CodexCategorySchema): Codex {
    const entries: CodexEntrySchema[] = [];
    const { codexEntryGroups } = this.context!.globalContext.generateStore.getState()
    const group = codexEntryGroups.find(group => group.category?.id === category.id)
    group?.children.forEach(entry => {
      entries.push(entry);
    })
    this.applyFilter(entries);
    return this;
  }
  byCodexDetail(detail: CodexDetail): Codex {
    this.applyFilter([detail.entry]);
    return this;
  }
  bySnippet(snippet: SnippetSchema): Codex {
    const entries: CodexEntrySchema[] = [];
    const { codexEntries } = this.context!.globalContext.generateStore.getState()
    codexEntries.forEach(entry => {
      if (snippet.attributes.content.includes(entry.attributes.name)) {
        entries.push(entry);
      }
    })
    this.applyFilter(entries);
    return this;
  }
  byText(text: string): Codex {
    const entries: CodexEntrySchema[] = [];
    const { codexEntries } = this.context!.globalContext.generateStore.getState()
    codexEntries.forEach(entry => {
      if (entry.attributes.name === text) {
        entries.push(entry);
      }
    })
    this.applyFilter(entries);
    return this;
  }
}

export const CodexUtils = Codex;